/*
    Created by Phat Hoang Pham
    phat@launchdeck.org
*/

import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { Image, Text, TouchableOpacity, View } from 'react-native-web';
import PropTypes from 'prop-types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { GoogleLogin, googleLogout, hasGrantedAllScopesGoogle, useGoogleLogin } from '@react-oauth/google';
import { cloneDeep } from 'lodash';

// Child Components
import CalendarPickerModal from './component_calendar_picker';

// Custom Components
import LoadingView from '../../components/component_loading_view';
import { Button, InScreenModal, NavBar } from '../../components';

// Utils
import { showAppError, showAlert, storeData } from '../../utils/util_helpers';
import { getDefaultSquareLocation } from '../../utils/util_main_app';

// APIs
import { switchAutoSync } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

export function AutoSyncModal(props, ref) {
    const { onGoogleDataUpdated, openGoogleLoginModalFunc } = props;

    // region Refs
    const calendarPickerModal = useRef(null);
    // endregion

    // region Global State
    const [truckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [locationList] = useGlobalState(STATE_KEYS.LOCATION_LIST);
    // endregion

    // region Local State
    const [googleData, setGoogleData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isTurningSync, setTurningSync] = useState(false);
    // endregion

    // region Function - Show Modal
    const showModal = () => {
        setGoogleData(
            GLOBAL.DATA.USER['google_email'] ?
                {
                    email: GLOBAL.DATA.USER['google_email'],
                    selectedCalendar: { name: GLOBAL.DATA.USER['sync_calendar_name'], email: GLOBAL.DATA.USER['sync_calendar_email'] },
                    syncOn: !!GLOBAL.DATA.USER['is_sync_calendar']
                }
                :
                null
        );
        setVisible(true);
    };
    // endregion

    // region Expose Methods
    useImperativeHandle(
        ref,
        () => ({
            showModal,
            hideModal: () => setVisible(false)
        }),
        [GLOBAL.DATA.USER]
    );
    // endregion

    // region Function - Switch Auto-Sync
    const goSwitchAutoSync = (customCalendar = null) => {
        setTurningSync(true);
        switchAutoSync(truckData.id, customCalendar, getDefaultSquareLocation()?.squareup_location_id)
            .then(() => {
                showModal();
                onGoogleDataUpdated();
            })
            .catch((error) => showAppError(error))
            .finally(() => setTurningSync(false));
    }
    // endregion

    // region Function - On Switch Pressed
    const onSwitchPressed = () => {
        showAlert({
            text: (
                googleData?.syncOn ?
                    'Are you sure you want to turn off Auto-Sync for your account?'
                    :
                    'By selecting Auto-Sync, all of your events on your calendar and any future changes you make will be reflected on Truckster.<br/><br/>Please ensure that your information is accurate including addresses and times. You can turn off Auto-Sync at any time.<br/><br/><b>Confirm to turn on Auto-Sync?</b>'
            ),
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => googleData?.syncOn ? goSwitchAutoSync() : calendarPickerModal.current?.showModal()
            }
        })
    }
    // endregion

    // region MAIN RENDER
    const showSquareSelectBeforeSync = ((locationList.length > 1) && !googleData?.syncOn);
    const isGlobalPreOrderingOn = !!truckData['pre_ordering'];
    return(
        <InScreenModal visible={visible}>
            {/* Nav Bar */}
            <NavBar title={'Auto Sync'} leftButtonOnPress={() => setVisible(false)} />

            {/* Main View */}
            <View style={{ flex: 1, width: '100%', backgroundColor: 'white', padding: 32 }}>
                {/* Title */}
                <Text style={{ fontWeight: '900', fontSize: 28, textAlign: 'left' }}>
                    Auto-Sync for Google Calendar
                </Text>

                {/* Description */}
                <Text style={{ marginTop: 16, fontSize: 18, color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'left', lineHeight: 26, marginBottom: 32 }}>
                    {
                        `This feature helps you save time from having to manually import events into Truckster.` +
                        (showSquareSelectBeforeSync ? '\n\nThe selected default Square location will be assigned to your synced event.' : '')
                    }
                </Text>

                {/* Current Signed In Email */}
                {
                    !!googleData &&
                    <TouchableOpacity
                        onPress={() => openGoogleLoginModalFunc(() => showModal())}
                        style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 32 }}>
                        <Image
                            source={require('../../images/image_google_color.png')}
                            style={{ width: 24, height: 24 }}
                        />

                        <Text style={{ fontSize: 18, marginLeft: 12 }}>
                            Signed in as{' '}
                            <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                                {googleData.email}
                            </Text>
                        </Text>
                    </TouchableOpacity>
                }

                {/* Turn sync button */}
                {
                    googleData ?
                        <Button
                            text={googleData?.syncOn ? 'Auto-Sync is ON  |  Click to turn off' : 'Auto-Sync is OFF  |  Click to turn on'}
                            style={{ backgroundColor: !googleData?.syncOn ? GLOBAL.COLOR.BASIC.UI_YELLOW : GLOBAL.COLOR.BASIC.UI_GREEN }}
                            useFeather={true}
                            image={!googleData?.syncOn ? 'cloud-off' : 'cloud'}
                            imageSize={24}
                            onPress={onSwitchPressed}
                            isLoading={isTurningSync}
                        />
                        :
                        <Button
                            text={'Sign to your Google account'}
                            style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE }}
                            useFeather={true}
                            image={require('../../images/iconSocialGoogle.png')}
                            imageSize={24}
                            onPress={() => openGoogleLoginModalFunc(() => showModal())}
                            isLoading={isLoading}
                        />
                }

                {/* If sync is on */}
                {
                    googleData?.syncOn &&
                    <View style={{ alignSelf: 'stretch', marginTop: 32 }}>
                        <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, textAlign: 'left', marginBottom: 16, lineHeight: 28 }}>
                            Your Google Calendar events within 90 days from today will be automatically imported to your Truckster Vendor calendar every hour.
                        </Text>

                        {/* Synced Email */}
                        <Text style={{ marginBottom: 16 }}>
                            Synced calendar:{' '}
                            <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: 'bold' }}>
                                {googleData?.selectedCalendar?.name}
                            </Text>
                        </Text>

                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Text style={{ lineHeight: 28 }}>
                                Global pre-ordering:
                                <Text style={{ fontWeight: 'bold' }}>
                                    {isGlobalPreOrderingOn ? ' ON' : ' OFF'}
                                </Text>
                            </Text>

                            <TouchableOpacity
                                onPress={() => showAlert({ text: `All of your events that are added through Auto-Sync in the future will have pre-ordering ${isGlobalPreOrderingOn ? 'enabled' : 'disabled'} by default, you can still manually turn it ${isGlobalPreOrderingOn ? 'off' : 'on'} for each individual event.<br/><br/>Global Pre-ordering can be turned on or off in the Order Settings screen.` })}
                                style={{ alignItems: 'center', justifyContent: 'center', marginLeft: 8 }}>
                                <Image
                                    source={require('../../images/iconLineInfo.png')}
                                    style={{ width: 24, height: 24 }}
                                    tintColor={GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY}
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                }

                {/* Calendar Picker */}
                <CalendarPickerModal
                    ref={calendarPickerModal}
                    onSelected={(email, name) => goSwitchAutoSync({ email, name })}
                />
            </View>
        </InScreenModal>
    )
    // endregion
}

AutoSyncModal = React.forwardRef(AutoSyncModal);

// region Props
AutoSyncModal.propTypes = {
    onGoogleDataUpdated: PropTypes.func,
    openGoogleLoginModalFunc: PropTypes.func
}

AutoSyncModal.defaultProps = {
    onGoogleDataUpdated: () => {},
    openGoogleLoginModalFunc: () => {}
}
// endregion
